/* import __COLOCATED_TEMPLATE__ from './add-replacement-item-modal.hbs'; */
/* RESPONSIBLE TEAM: team-integration-apps */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { restartableTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import ajax from 'embercom/lib/ajax';
import type IntlService from 'ember-intl/services/intl';
import type { Order, LineItem } from './types';
import type Session from 'embercom/services/session';
import { captureException } from 'embercom/lib/sentry';
import {
  hasAllItemReasons,
  type LineItemReason,
} from 'embercom/lib/inbox/shopify/compensation-reasons';
import type ShopifyCompensationConfig from 'embercom/services/shopify-compensation-config';

interface SelectedLineItem {
  lineItemId: number;
  quantity: number;
}

interface AddReplacementItemModalArgs {
  isOpen: boolean;
  onModalClose: () => void;
  order: Order;
  shopifyDomain: string | null;
  onReplacementSuccess?: () => void;
  conversationId?: number;
}

export default class AddReplacementItemModal extends Component<AddReplacementItemModalArgs> {
  @service declare notificationsService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;
  @service declare session: Session;
  @service declare intl: IntlService;
  @service declare shopifyCompensationConfig: ShopifyCompensationConfig;

  @tracked note = '';
  @tracked notifyCustomer = false;
  @tracked selectedLineItems: SelectedLineItem[] = [];
  @tracked lineItemReasons: LineItemReason[] = [];
  @tracked isSelectAllChecked = false;
  @tracked genericRefundReason = '';
  @tracked workspaceRefundReasons: string[] = [];
  @tracked workspaceRefundReasonsFetchError: string | null = null;

  get isPerItemRefundReasonEnabled() {
    return this.shopifyCompensationConfig.perItemRefundReasonEnabled;
  }

  get workspaceReasonOptions() {
    return this.workspaceRefundReasons.map((reason) => ({
      value: reason,
      label: reason,
    }));
  }

  get isFetchingWorkspaceRefundReasons(): boolean {
    return taskFor(this.fetchWorkspaceRefundReasons).isRunning;
  }

  get hasWorkspaceRefundReasonsError(): boolean {
    return Boolean(this.workspaceRefundReasonsFetchError);
  }

  get shouldShowEmptyReasonsGuidance(): boolean {
    return (
      !this.isFetchingWorkspaceRefundReasons &&
      !this.hasWorkspaceRefundReasonsError &&
      this.workspaceRefundReasons.length === 0 &&
      this.isPerItemRefundReasonEnabled &&
      !this.isSelectAllChecked
    );
  }

  get hasAllRequiredReasons(): boolean {
    if (this.isSelectAllChecked) {
      return Boolean(this.genericRefundReason);
    }
    if (!this.isPerItemRefundReasonEnabled) {
      return Boolean(this.note.trim());
    }
    return hasAllItemReasons(this.selectedLineItems, this.lineItemReasons);
  }

  get getLineItemReason() {
    void this.lineItemReasons.length;

    return (lineItemId: number): string => {
      return this.lineItemReasons.find((r) => r.lineItemId === lineItemId)?.reason ?? '';
    };
  }

  constructor(owner: unknown, args: AddReplacementItemModalArgs) {
    super(owner, args);
    this.resetModalData();

    if (this.isPerItemRefundReasonEnabled) {
      void taskFor(this.fetchWorkspaceRefundReasons).perform();
    }
  }

  get currencySymbol() {
    return this.args.order.total?.charAt(0) ?? '$';
  }

  get hasSelectedItems() {
    return this.selectedLineItems.some((item) => item.quantity > 0);
  }

  get isAddReplacementTaskRunning() {
    return taskFor(this.addReplacementTask).isRunning;
  }

  get isLineItemReplaceable() {
    return (lineItem: LineItem): boolean => {
      if (lineItem.gift_card) {
        return false;
      }
      let alreadyRefunded = lineItem.already_refunded ?? 0;
      let quantityReplaceable = lineItem.quantity_refundable ?? lineItem.quantity - alreadyRefunded;
      return quantityReplaceable > 0;
    };
  }

  @action
  isLineItemSelected(lineItemId: number): boolean {
    let selectedItem = this.selectedLineItems.find((item) => item.lineItemId === lineItemId);
    return selectedItem ? selectedItem.quantity > 0 : false;
  }

  @action
  getSelectedQuantity(lineItemId: number): number {
    let selectedItem = this.selectedLineItems.find((item) => item.lineItemId === lineItemId);
    return selectedItem?.quantity || 0;
  }

  @action
  updateLineItemReason(lineItemId: number, event: Event) {
    let target = event.target as HTMLSelectElement;
    let reason = target.value;
    let existingIndex = this.lineItemReasons.findIndex((r) => r.lineItemId === lineItemId);

    if (existingIndex >= 0) {
      this.lineItemReasons = this.lineItemReasons.map((r) =>
        r.lineItemId === lineItemId ? { ...r, reason } : r,
      );
    } else {
      this.lineItemReasons = [...this.lineItemReasons, { lineItemId, reason }];
    }
  }

  @action
  updateGenericRefundReason(event: Event) {
    let target = event.target as HTMLSelectElement;
    this.genericRefundReason = target.value;
  }

  @action
  retryFetchWorkspaceRefundReasons() {
    void taskFor(this.fetchWorkspaceRefundReasons).perform();
  }

  @action
  toggleSelectAll() {
    this.isSelectAllChecked = !this.isSelectAllChecked;
    if (this.isSelectAllChecked) {
      if (
        this.workspaceRefundReasons.length === 0 &&
        !taskFor(this.fetchWorkspaceRefundReasons).isRunning
      ) {
        void taskFor(this.fetchWorkspaceRefundReasons).perform();
      }
      this.selectedLineItems = this.args.order.line_items
        .filter((item) => item.id && this.isLineItemReplaceable(item))
        .map((item) => ({
          lineItemId: item.id,
          quantity: item.quantity_refundable ?? item.quantity,
        }));
      this.lineItemReasons = [];
    } else {
      this.selectedLineItems = this.args.order.line_items
        .filter((item) => item.id)
        .map((item) => ({ lineItemId: item.id, quantity: 0 }));
    }
  }

  @action
  toggleLineItemSelection(lineItemId: number) {
    let isCurrentlySelected = this.isLineItemSelected(lineItemId);
    let lineItem = this.args.order.line_items.find((item) => item.id === lineItemId);

    if (!lineItem) {
      return;
    }

    let targetQuantity = isCurrentlySelected ? 0 : lineItem.quantity;
    this.updateLineItemQuantityValue(lineItemId, targetQuantity);
  }

  @action
  updateLineItemQuantity(lineItemId: number, event: Event) {
    let target = event.target as HTMLInputElement;
    let quantity = parseInt(target.value, 10);
    this.updateLineItemQuantityValue(lineItemId, quantity);
  }

  private updateLineItemQuantityValue(lineItemId: number, quantity: number) {
    let existingItemIndex = this.selectedLineItems.findIndex(
      (item) => item.lineItemId === lineItemId,
    );

    if (existingItemIndex !== -1) {
      this.selectedLineItems[existingItemIndex] = {
        ...this.selectedLineItems[existingItemIndex],
        quantity: Math.max(0, quantity),
      };
    } else {
      this.selectedLineItems = [
        ...this.selectedLineItems,
        {
          lineItemId,
          quantity: Math.max(0, quantity),
        },
      ];
    }

    this.selectedLineItems = [...this.selectedLineItems];
    this.isSelectAllChecked = false;
  }

  @action
  resetModalData() {
    this.note = '';
    this.notifyCustomer = false;
    this.lineItemReasons = [];
    this.isSelectAllChecked = false;
    this.genericRefundReason = '';
    this.selectedLineItems = this.args.order.line_items
      .filter((item) => item.id)
      .map((item) => ({
        lineItemId: item.id,
        quantity: 0,
      }));
  }

  @action
  handleModalClose() {
    this.resetModalData();
    this.args.onModalClose();
  }

  @restartableTask
  *addReplacementTask(): TaskGenerator<void> {
    try {
      let itemsToAdd = this.selectedLineItems.filter((item) => item.quantity > 0);

      if (itemsToAdd.length === 0) {
        this.notificationsService.notifyError(
          this.intl.t(
            'inbox.conversation-details-sidebar.shopify.add-replacement-modal.errors.no-items-selected',
          ),
        );
        return;
      }

      if (!this.hasAllRequiredReasons) {
        this.notificationsService.notifyError(
          this.intl.t(
            'inbox.conversation-details-sidebar.shopify.add-replacement-modal.errors.no-reason',
          ),
        );
        return;
      }

      for (let selectedItem of itemsToAdd) {
        let lineItem = this.args.order.line_items.find((li) => li.id === selectedItem.lineItemId);

        let reason: string;
        if (this.isSelectAllChecked) {
          reason = this.genericRefundReason;
        } else if (this.isPerItemRefundReasonEnabled) {
          reason =
            this.lineItemReasons.find((r) => r.lineItemId === selectedItem.lineItemId)?.reason ??
            '';
        } else {
          reason = this.note;
        }

        yield ajax({
          url: '/ember/inbox/shopify/add_replacement_item',
          type: 'POST',
          data: JSON.stringify({
            app_id: this.session.workspace.id,
            order_id: this.args.order.id,
            order_name: this.args.order.name,
            conversation_id: this.args.conversationId,
            line_item_id: selectedItem.lineItemId,
            line_item_title: lineItem?.title,
            quantity: selectedItem.quantity,
            reason,
            notify_customer: this.notifyCustomer,
            shopify_domain: this.args.shopifyDomain,
          }),
        });
      }

      this.notificationsService.notifyConfirmation(
        this.intl.t('inbox.conversation-details-sidebar.shopify.add-replacement-modal.success', {
          count: itemsToAdd.length,
        }),
      );

      this.intercomEventService.trackAnalyticsEvent({
        action: 'added_replacement_items',
        object: 'shopify_order',
        section: 'inbox_sidebar',
        shopify_domain: this.args.shopifyDomain,
        items_count: itemsToAdd.length,
        notify_customer: this.notifyCustomer,
      });

      this.handleModalClose();
      this.args.onReplacementSuccess?.();
    } catch (error) {
      this.notificationsService.notifyError(
        (error as Error).message ||
          this.intl.t(
            'inbox.conversation-details-sidebar.shopify.add-replacement-modal.errors.generic',
          ),
      );
    }
  }

  @restartableTask
  *fetchWorkspaceRefundReasons(): TaskGenerator<void> {
    this.workspaceRefundReasonsFetchError = null;

    try {
      let response: any = yield ajax({
        type: 'GET',
        url: `/ember/shopify/integrations/workspace_refund_reasons?app_id=${this.session.workspace.id}`,
      });

      if (response && response.reasons) {
        this.workspaceRefundReasons = response.reasons;
      }
    } catch (error: unknown) {
      captureException(error);
      this.workspaceRefundReasonsFetchError = this.intl.t(
        'inbox.conversation-details-sidebar.shopify.refund-order-modal.refund-reasons-fetch-error',
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::Shopify::AddReplacementItemModal': typeof AddReplacementItemModal;
    'inbox2/conversation-details-sidebar/sidebar-sections/shopify/add-replacement-item-modal': typeof AddReplacementItemModal;
  }
}
